/**
 * Read-through glue between the axios instance and the offline read cache.
 *
 * Kept out of `api.js` on purpose: `api.js` cannot be imported in a test without
 * dragging in axios, the toast bus and the auth interceptors, so the interesting
 * decisions - is this failure a dead link? is this GET cacheable? what does the
 * served-from-cache response look like? - live here as pure functions over the
 * request config and the cached record.
 */
import { buildReadCacheKey, readCachedResponse, saveCachedResponse } from './offlineReadCache';
import { currentUserScope } from './sessionCache';

/**
 * Endpoints that must never be answered from storage: a stale profile or a
 * cached "ok" from an auth route would be a security lie rather than a
 * convenience.
 */
const NEVER_CACHED_PATHS = [/\/auth\//i, /\/login\b/i, /\/logout\b/i, /\/refresh\b/i];

/** True when the browser itself says there is no link. */
export const isBrowserOffline = () => {
  try {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  } catch (_) {
    return false;
  }
};

/**
 * A failure that says "the request never got an answer".
 *
 * A timeout counts: on a till with weak wifi, a 30-second hang followed by an
 * empty screen is worse than the last known invoice list. A 401/403/500 is a
 * real answer and must never be papered over with cached data.
 */
export const isNetworkFailure = (error) => {
  if (!error) return false;
  if (error.isAccessDenied) return false;
  if (error.code === 'ERR_CANCELED') return false;
  if (error.response) return false;
  return true;
};

/** Only plain JSON GETs are cached - writes, uploads and blobs are not. */
export const isCacheableGet = (config) => {
  if (!config) return false;
  const method = String(config.method || 'get').toLowerCase();
  if (method !== 'get') return false;
  // Never silently show stale business data. A caller must explicitly opt in
  // and label the returned `offlineCache` response as stale in its own UI.
  if (config.meta?.offlineCache !== true || currentUserScope() === 'anon') return false;
  if (config.responseType && config.responseType !== 'json') return false;
  const url = String(config.url || '');
  if (!url) return false;
  return !NEVER_CACHED_PATHS.some((pattern) => pattern.test(url));
};

const cacheKeyFor = (config) => buildReadCacheKey({
  method: config?.method || 'get',
  baseURL: config?.baseURL || '',
  url: config?.url || '',
  params: config?.params || null,
  scope: currentUserScope()
});

/**
 * Persist a successful GET for the next outage.
 *
 * Deliberately returns a promise (the caller ignores it): storage is slow and
 * must never delay rendering a response that already arrived.
 */
export const rememberGetResponse = (config, response) => {
  if (!isCacheableGet(config)) return Promise.resolve(null);
  if (!response || response.offlineCache) return Promise.resolve(null);
  // 304 means "your copy is current" and carries no body to store.
  if (Number(response.status) === 304) return Promise.resolve(null);
  if (response.data === undefined || response.data === null) return Promise.resolve(null);
  return saveCachedResponse(cacheKeyFor(config), response.data);
};

/**
 * Read the last known good payload for a GET *without* waiting for the network.
 *
 * `readOfflineFallback` below is the failure path: it is only reached once the
 * request has been given up on. A screen that wants to paint the previous result
 * first - and then replace it when the live answer lands - needs the same lookup
 * *before* the request, which is what this is for.
 *
 * Returns `null` when nothing usable is stored (never cached, pruned, expired,
 * or cached for a different signed-in user); the caller then just waits for the
 * network as before.
 */
export const peekCachedGet = async (config) => {
  if (!isCacheableGet(config)) return null;
  const cached = await readCachedResponse(cacheKeyFor(config));
  if (!cached) return null;
  return {
    payload: cached.payload,
    savedAt: cached.savedAt,
    ageMs: cached.ageMs,
    stale: cached.stale
  };
};

/**
 * Build an axios-shaped response out of a cached payload.
 *
 * `data` is returned byte-for-byte as the server sent it, so every existing
 * `res.data.data` / `res.data.pagination` reader keeps working; the fact that it
 * came from disk is reported on the response object instead of being mixed into
 * the payload.
 */
export const readOfflineFallback = async (config) => {
  if (!isCacheableGet(config)) return null;
  const cached = await readCachedResponse(cacheKeyFor(config));
  if (!cached) return null;

  return {
    data: cached.payload,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    request: null,
    offlineCache: true,
    offlineCachedAt: cached.savedAt,
    offlineAgeMs: cached.ageMs
  };
};
